import { useMemo } from 'react';

import { useAsyncAtom } from '@service-initiation/shared-utils-jotai';
import {
  selectedAppointmentAsyncAtom,
  useCompanyIdValue,
  useCustomerDetailsValue,
  useDeliveryMethodValue,
  useDeviceDetailsAsyncAtom,
  useFullAddressValue,
  useGeolocationValue,
  useLeadIdValue,
  usePostalCodeValue,
  useRepairMessageValue,
  useSelectedDeviceTypeAsyncAtom,
  useSelectedRepairsAsyncAtom,
  useSelectedStoreAsyncAtom,
} from '@service-initiation/tech-care-data-access-store';

import {
  flattenDeviceDetailsAnalytics,
  removeSingleQuotes,
  removeUndefinedEmptyAndNullValues,
} from '../utils';

export const useSanitizedGlobalUserState = () => {
  const { value: appointment } = useAsyncAtom(selectedAppointmentAsyncAtom);

  const deliveryMethod = useDeliveryMethodValue();
  const { value: selectedRepairs } = useSelectedRepairsAsyncAtom();
  const repairMessage = useRepairMessageValue();
  const companyId = useCompanyIdValue();
  const fullAddress = useFullAddressValue();
  const geolocation = useGeolocationValue();
  const postalCode = usePostalCodeValue();
  const leadId = useLeadIdValue();

  const customerDetails = useCustomerDetailsValue();
  const { value: deviceDetails } = useDeviceDetailsAsyncAtom();
  const { value: deviceType } = useSelectedDeviceTypeAsyncAtom();
  const { value: selectedStore } = useSelectedStoreAsyncAtom();

  // some values are duplicated due to keeping event names consistent with v1. Will ask analytics team to move to v2 versions after migrating.
  return useMemo(
    () =>
      removeUndefinedEmptyAndNullValues({
        ...(deviceDetails ? flattenDeviceDetailsAnalytics(deviceDetails) : {}),
        Company: companyId, //v1
        CompanyId: companyId,
        ...(selectedRepairs && selectedRepairs.length > 0
          ? {
              DeviceDamages: selectedRepairs.map(
                (repair) => repair.repairType.symptom
              ),
            }
          : {}),
        Device: deviceDetails?.id,
        DeviceDetail1: deviceDetails?.family, //v1
        DeviceDetail2: deviceDetails?.name, //v1
        DeviceType: removeSingleQuotes(deviceType?.name),
        Fulfillment: deliveryMethod, //v1
        FullAddress: fullAddress,
        GeoLocation: geolocation,
        LeadId: leadId,
        PhoneNumberProvided: !!customerDetails?.phoneNumber?.length,
        PostalCode: postalCode,
        Repair: selectedRepairs?.map((repair) => repair.id).toString(),
        SelectedDate: appointment?.date,
        SelectedTime: appointment?.startTime,
        Store: selectedStore?.id, //v1
        StoreId: selectedStore?.id,
        StoreName: selectedStore?.name,
        StoreType: selectedStore?.store_type,
        ZipCode: postalCode, //v1
        customerMessage: repairMessage
          ? 'customer_message_filled'
          : 'customer_message_not_filled',
        deliveryMethod: deliveryMethod,
      }),
    [
      deviceDetails,
      companyId,
      selectedRepairs,
      deviceType?.name,
      deliveryMethod,
      fullAddress,
      geolocation,
      leadId,
      customerDetails?.phoneNumber?.length,
      postalCode,
      appointment?.date,
      appointment?.startTime,
      selectedStore?.id,
      selectedStore?.name,
      selectedStore?.store_type,
      repairMessage,
    ]
  );
};
